import { createPsAxiosInstance } from '@patsnap/common-ps-http';
import axios from 'axios';
import { requestConfigWrapper, PersistentCacheConfig, PersistentCacheStrategy } from '@patsnap/common-ps-http/dist/PersistentCache';
/**
 * sso限制类型
 */
export var E_SSORestrictType;
(function (E_SSORestrictType) {
    /**
     * 钉钉
     */
    E_SSORestrictType["DINGTLK"] = "DINGTLK";
    /**
     * 企业微信
     */
    E_SSORestrictType["WECHAT_ENTERPRISE"] = "WECHAT_ENTERPRISE";
    /**
     * 飞书
     */
    E_SSORestrictType["FEISHU"] = "FEISHU";
    /**
     * JWT
     */
    E_SSORestrictType["JWT"] = "JWT";
    /**
     * 其他或未知
     */
    E_SSORestrictType["UNKNOWN"] = "UNKNOWN";
})(E_SSORestrictType || (E_SSORestrictType = {}));
export var UserType;
(function (UserType) {
    UserType["TRIAL"] = "TRIAL";
    UserType["COMMERCIAL"] = "COMMERCIAL";
    UserType["FREEMIUM"] = "FREEMIUM";
})(UserType || (UserType = {}));
const MOCK_SITE_REGION = '_MOCK_SITE_REGION_';
const SUPPORT_SITE_REGIONS = ['CN', 'US', 'EU'];
export var ErrorCode;
(function (ErrorCode) {
    /**
     * 需要进行 MFA 验证
     */
    ErrorCode[ErrorCode["MFA_VERIFICATION_REQUIRED"] = 32001] = "MFA_VERIFICATION_REQUIRED";
})(ErrorCode || (ErrorCode = {}));
export class Account {
    get _info() {
        const { site_region } = this._info_;
        return {
            ...this._info_,
            ...{
                get site_region() {
                    try {
                        let siteRegion;
                        [, siteRegion] = window.location.search.match(/_MOCK_SITE_REGION_\=(EU|CN|US)/) || [];
                        if (siteRegion && SUPPORT_SITE_REGIONS.includes(siteRegion)) {
                            return siteRegion;
                        }
                        siteRegion = localStorage.getItem(MOCK_SITE_REGION);
                        if (siteRegion && SUPPORT_SITE_REGIONS.includes(siteRegion)) {
                            return siteRegion;
                        }
                    }
                    catch (e) {
                        console.log(e);
                    }
                    return site_region;
                }
            }
        };
    }
    constructor(info) {
        this._info_ = info;
    }
    /**
     * 当前用户是否为特殊 IP 登录
     */
    get isSpecialIp() {
        return this._info.special_ip;
    }
    /**
     * 是否登录
     */
    get isLogin() {
        return !!this._info.account_id;
    }
    /**
     * 网站服务区域
     */
    get siteRegion() {
        return this._info.site_region;
    }
    get whiteLabel() {
        var _a;
        return (_a = this._info) === null || _a === void 0 ? void 0 : _a.company_label_switcher;
    }
    get isHighLevelWhiteLabel() {
        return this.whiteLabel && !!this._info.company_label_high;
    }
    get customLogoUrl() {
        var _a;
        return this.whiteLabel && ((_a = this._info) === null || _a === void 0 ? void 0 : _a.company_logo);
    }
    /**
     * 自定义纯白色 logo
     */
    get customWhiteLogoUrl() {
        var _a;
        return this.whiteLabel && ((_a = this._info) === null || _a === void 0 ? void 0 : _a.company_logo_white);
    }
    get thirdParty() {
        let obj = {};
        const { qiyu_key, zendesk_key } = this._info.third_party_attribute_config;
        /* High Level 白标签，移除七鱼 */
        if (!this.isHighLevelWhiteLabel && qiyu_key) {
            obj['qiyu'] = {
                key: qiyu_key,
                email: this._info.email,
                companyName: this._info.company_name,
                isMultiLogin: this._info.multi_login,
                mobileNumber: this._info.mobile_number,
                manualCompany: this._info.manual_company
            };
        }
        if (zendesk_key) {
            obj['zendesk'] = { key: zendesk_key };
        }
        /* sentry  */
        obj['sentry'] = {
            email: this._info.email,
            accountId: this._info.encrypted_account_id,
            companyId: this._info.encrypted_company_id,
            companyName: this._info.company_name,
            needTrack: !!this._info.third_party_attribute_config.need_track,
            userType: this._info.account_type,
        };
        return obj;
    }
    get companyName() {
        return this._info.company_name;
    }
    get username() {
        return this._info.nickname;
    }
    get mobileNumber() {
        return this._info.mobile_number;
    }
    get userPackageType() {
        var _a;
        return (_a = this._info.user_package) === null || _a === void 0 ? void 0 : _a.type;
    }
    get userPackageEndTime() {
        var _a;
        return (_a = this._info.user_package) === null || _a === void 0 ? void 0 : _a.time_end;
    }
    get email() {
        return this._info.email;
    }
    get canAccessHomePage() {
        return (this._info.granted_products || []).map(({ name }) => name).includes('home');
    }
    get tenants() {
        var _a;
        const tenants = (_a = (this._info.tenants || [])) === null || _a === void 0 ? void 0 : _a.map((tenant) => {
            var _a, _b;
            const disabled = tenant.domain_restrict || tenant.ip_restrict || tenant.sso_restrict || !tenant.account_status_active;
            return {
                ...tenant,
                disabled,
                classname: disabled ? 'disabled' : '',
                key: tenant.tenant_id,
                label: tenant.tenant_name,
                active: tenant.tenant_id === ((_a = this._info) === null || _a === void 0 ? void 0 : _a.company_id) || (!tenant.tenant_id && !((_b = this._info) === null || _b === void 0 ? void 0 : _b.company_id))
            };
        });
        // 将数组 tenants中属性active为true的元素放到数组的最前面, disabled 为true的放到最后面, 其他顺序不变
        tenants.sort((a, b) => {
            if (a.active && !b.active) {
                return -1;
            }
            else if (!a.active && b.active) {
                return 1;
            }
            else if (a.disabled && !b.disabled) {
                return 1;
            }
            else if (!a.disabled && b.disabled) {
                return -1;
            }
            else {
                return 0;
            }
        });
        return tenants;
    }
}
export const getAccountInfo = (opt) => {
    return Promise.all([
        getCommonData(opt),
        getExtraInfo(opt),
    ]).then(([common, additional]) => {
        const data = { ...common, ...additional };
        return data;
    });
};
const defaultCacheConfigOptions = {
    strategy: PersistentCacheStrategy.CACHE_FIRST,
    storagePrefix: 'v1.9.0',
    VaryHeadersArray: ['authorization', 'x-api-version']
};
const commonDataCacheConfig = {
    ...defaultCacheConfigOptions,
    detectAllowCachingHook(resp) {
        if (!resp.ok) {
            return false;
        }
        return resp.json().then(body => {
            return body.status && body.data && body.data.common_products;
        }).catch(( /* err */) => false);
    }
};
const basicDataCacheConfig = {
    ...defaultCacheConfigOptions,
    strategy: PersistentCacheStrategy.CACHE_FIRST_WITH_SILENT_UPDATE,
    detectAllowCachingHook(resp) {
        if (!resp.ok) {
            return false;
        }
        return resp.json().then(body => {
            return body.status && body.data && body.data.account_id;
        }).catch(( /* err */) => false);
    }
};
const extraDataCacheConfig = {
    ...defaultCacheConfigOptions,
    detectAllowCachingHook(resp) {
        if (!resp.ok) {
            return false;
        }
        return resp.json().then(body => {
            return body.status && body.data && body.data.account_id;
        }).catch(( /* err */) => false);
    }
};
export const getCommonData = (opt) => {
    const _axios = opt.http || axios;
    const cache = opt.cache === undefined ? true : !!opt.cache;
    const url = `${opt.uri}/user-service/account-info/common-data`;
    const fetchConfig = requestConfigWrapper({
        url,
        cache
    }, new PersistentCacheConfig(commonDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => res.data.data)
        .catch(() => {
        return {};
    });
};
export const getExtraInfo = (opt) => {
    const _axios = opt.http || axios;
    const cache = opt.cache === undefined ? true : !!opt.cache;
    const url = `${opt.uri}/user-service/account-info/extra-data`;
    const fetchConfig = requestConfigWrapper({
        url,
        uuid: url,
        cache
    }, new PersistentCacheConfig(extraDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => res.data.data)
        .catch(() => {
        return {};
    });
};
/**
 * 获取仅有用户信息的数据
 */
export const getBasicAccountInfo = (opt) => {
    const _axios = opt.http || axios;
    const cache = false;
    const url = `${opt.uri}/user-service/account-info/basic-data`;
    const fetchConfig = requestConfigWrapper({
        url,
        uuid: url,
        cache
    }, new PersistentCacheConfig(basicDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => res.data.data)
        .catch(() => {
        return {};
    });
};
export const getTenants = (opt) => {
    const _axios = opt.http || axios;
    const cache = opt.cache === undefined ? true : !!opt.cache;
    const url = `${opt.uri}/user-service/tenant/tenant-select-infos`;
    const fetchConfig = requestConfigWrapper({
        url,
        uuid: url,
        cache
    }, new PersistentCacheConfig(basicDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => res.data)
        .catch(() => {
        return [];
    });
};
/**
 * 切换租户 passport接口
 * 切换后相当于重新登陆，需要刷新页面
 */
export const postSwitchTenant = (opt, params) => {
    const _axios = opt.http || axios;
    const url = `${opt.uri}/switch-tenant`;
    const fetchConfig = requestConfigWrapper({
        url,
        uuid: url,
        params,
        withCredentials: true,
        method: 'POST',
    }, new PersistentCacheConfig(basicDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => res.data);
};
/**
 * 租户切换成功后跳转有权限的产品页
 */
export const postLoginSuccessPageApi = (opt, params) => {
    const _axios = createPsAxiosInstance().psHttp;
    const url = `${opt.uri}/user-service/account-login/success-page`;
    const fetchConfig = requestConfigWrapper({
        url,
        data: params,
        headers: {
            ...opt.headers
        },
        method: 'POST',
    }, new PersistentCacheConfig(basicDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => res.data.data)
        .catch(() => {
        return {};
    });
};
export const getNewProducts = (opt) => {
    const _axios = opt.http || axios;
    const cache = opt.cache === undefined ? true : !!opt.cache;
    const url = `${opt.uri}/user-service/account-info/new-products`;
    const fetchConfig = requestConfigWrapper({
        url,
        uuid: url,
        cache,
    }, new PersistentCacheConfig(basicDataCacheConfig));
    return _axios(fetchConfig)
        .then(res => { var _a; return ((_a = res.data) === null || _a === void 0 ? void 0 : _a.data) || []; })
        .catch(() => {
        return [];
    });
};
//# sourceMappingURL=apis.js.map